import { useState, useEffect, useRef } from 'react'
import './BannerSlider.css'

export interface BannerSlide {
  id: string | number
  image: string
  title?: string
  subtitle?: string
  description?: string
  link?: string
  buttonText?: string
}

interface BannerSliderProps {
  slides: BannerSlide[]
  autoplay?: boolean
  autoplayInterval?: number
  showArrows?: boolean
  showIndicators?: boolean
}

const BannerSlider = ({
  slides,
  autoplay = true,
  autoplayInterval = 5000,
  showArrows = true,
  showIndicators = true
}: BannerSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const intervalRef = useRef<number | null>(null)

  // Auto-play functionality
  useEffect(() => {
    if (autoplay && !isHovered && slides.length > 1) {
      intervalRef.current = window.setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length)
      }, autoplayInterval)
    }

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current)
      }
    }
  }, [autoplay, autoplayInterval, isHovered, slides.length])

  const goToSlide = (index: number) => {
    setCurrentIndex(index)
    // Reset autoplay timer when manually changing slides
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
  }

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? slides.length - 1 : currentIndex - 1
    goToSlide(newIndex)
  }

  const goToNext = () => {
    const newIndex = (currentIndex + 1) % slides.length
    goToSlide(newIndex)
  }

  if (!slides || slides.length === 0) {
    return null
  }

  return (
    <div
      className="banner-slider"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="slider-container">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`slide ${index === currentIndex ? 'active' : ''} ${slide.link ? 'slide-link' : ''}`}
            style={{
              backgroundImage: `url(${slide.image})`,
              opacity: index === currentIndex ? 1 : 0,
              zIndex: index === currentIndex ? 1 : 0
            }}
          >
            {/* Chỉ hiển thị overlay và text nếu có nội dung text */}
            {(slide.title || slide.subtitle || slide.description) && (
              <>
                <div className="slide-overlay"></div>
                <div className="slide-content">
                  <div className="slide-content-inner">
                    {slide.subtitle && (
                      <p className="slide-subtitle">{slide.subtitle}</p>
                    )}
                    {slide.title && (
                      <h2 className="slide-title">{slide.title}</h2>
                    )}
                    {slide.description && (
                      <p className="slide-description">{slide.description}</p>
                    )}
                    {slide.link && slide.buttonText && (
                      <a href={slide.link} className="slide-button">
                        {slide.buttonText}
                        <i className="fas fa-arrow-right"></i>
                      </a>
                    )}
                  </div>
                </div>
              </>
            )}
            {/* Nếu có link nhưng không có text, wrap toàn bộ slide trong link */}
            {slide.link && !(slide.title || slide.subtitle || slide.description) && (
              <a 
                href={slide.link} 
                className="slide-full-link"
                aria-label="Xem chi tiết"
              ></a>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {showArrows && slides.length > 1 && (
        <>
          <button
            className="slider-arrow slider-arrow-prev"
            onClick={goToPrevious}
            aria-label="Slide trước"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <button
            className="slider-arrow slider-arrow-next"
            onClick={goToNext}
            aria-label="Slide sau"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </>
      )}

      {/* Indicators */}
      {showIndicators && slides.length > 1 && (
        <div className="slider-indicators">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              className={`indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Chuyển đến slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default BannerSlider
